import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { I18nReactContext } from '../../../contexts/I18nContext';
import { CreateEditNewSignDocModal } from '../../LearningManagement/CreateNewSignDocButton/CreateEditNewSignDocModal/CreateEditNewSignDocModal';
import { SignDocListRefresherContext } from '../../../contexts/SignDocListRefresherContext';
import { SignDocVersionCard } from '../SignDocVersionCard/SignDocVersionCard';
import { PageTitle } from '../../PageTitle/PageTitle';
import { getSignDocVersionsAPI } from '../../../Services/api';
import { SIGNDOC_STATUS, SignDocTypes } from '../SignDocLib';
import { find, get, map } from 'lodash';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import './SignDocVersionManagementPage.scss';

/**
 * SignDoc types that support version creation (duplication)
 * Must match backend constant: SignDocTypeAllowedToBeDuplicated in SignDocLib.js
 */
const TYPES_THAT_DOESNT_SUPPORT_VERSIONING = [SignDocTypes.REGULAR_COURSE];

/**
 * SignDoc Version Management Page
 *
 * This page provides version control functionality for SignDocs, allowing admins to:
 * - View all versions of a SignDoc (V1, V2, V3, etc.)
 * - Create new versions (duplicates with version tracking)
 * - Manage version lifecycle (active/archived/scheduled status)
 * - Handle sequential or parallel version groups
 *
 * Features implemented in phases:
 * Phase 1: UI with mock data
 * Phase 2: Backend integration
 * Phase 3: Version creation workflow
 * Phase 4: Status management
 */
export const SignDocVersionManagementPage = () => {
    const { signDocId } = useParams();
    const navigate = useNavigate();
    const { i18n } = useContext(I18nReactContext);
    const { refreshSignDocList } = useContext(SignDocListRefresherContext);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [signDoc, setSignDoc] = useState(null);
    const [versions, setVersions] = useState([]);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState(null);

    useEffect(() => {
        if (signDocId) {
            fetchVersions();
        }
    }, [signDocId]);

    /**
     * Fetch all versions of the SignDoc group from the backend
     * Replaces the previous mock data implementation (Phase 2)
     */
    const fetchVersions = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch all versions using the groupRootId
            const versionsData = await getSignDocVersionsAPI({ signDocId });

            // Find the root SignDoc (groupVersion === 0) or use the first version
            const rootSignDoc = find(versionsData, { status: SIGNDOC_STATUS.ACTIVE }) || find(versionsData, v => { return v.id === v.groupRootId });

            setSignDoc(rootSignDoc);
            setVersions(versionsData);
        } catch (err) {
            console.error('Error fetching SignDoc versions:', err);
            setError(i18n.translate('errors.generalServerError'));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle creating a new version
     * Opens the create modal for duplicating with version tracking
     */
    const handleCreateVersion = () => {
        setIsCreateModalOpen(true);
    };

    /**
     * Handle closing create modal
     */
    const handleCloseCreateModal = () => {
        setIsCreateModalOpen(false);
    };

    /**
     * Handle after version created successfully
     * Refreshes the version list and closes modal
     */
    const handleAfterCreateComplete = () => {
        setIsCreateModalOpen(false);
        refreshSignDocList();
        // Reload versions from API to get the newly created version
        fetchVersions();
    };

    /**
     * Handle editing a specific version
     * Opens edit modal with selected version data
     */
    const handleEditVersion = (version) => {
        setSelectedVersion(version);
        setIsEditModalOpen(true);
    };

    /**
     * Handle closing edit modal
     */
    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedVersion(null);
    };

    /**
     * Handle after version edited successfully
     * Refreshes the version list and closes modal
     */
    const handleAfterEditComplete = () => {
        setIsEditModalOpen(false);
        setSelectedVersion(null);
        refreshSignDocList();
        // Reload versions from API to get the updated version data
        fetchVersions();
    };

    if (loading) {
        return (
            <LoadingSpinner />
        );
    }

    if (!signDoc || error) {
        return (
            <div className="SignDocVersionManagementPage" data-testid="version-management-page">
                <div className="empty-state" data-testid="empty-state">
                    <h2>{i18n.translate('signDocVersionManagement.notFound')}</h2>
                    <p>{!error ? i18n.translate('signDocVersionManagement.notFoundDescription') : error}</p>
                    <button
                        className="back-button"
                        onClick={() => navigate(-1)}
                        data-testid="empty-back-button"
                    >
                        {i18n.translate('general.back')}
                    </button>
                </div>
            </div>
        );
    }

    // Check if the current SignDoc type supports versioning/duplication
    const canCreateVersion = !TYPES_THAT_DOESNT_SUPPORT_VERSIONING.includes(signDoc.type);

    return (
        <div className="SignDocVersionManagementPage MenuPage" data-testid="version-management-page">
            <PageTitle
                title={i18n.translate('signDocVersionManagement.title')}
                subTitle={i18n.translate('signDocVersionManagement.managingVersionsFor')}
                subTitleBold={get(signDoc, 'docName', '')}
                size="small"
            />
            <div className="menuInnerPage">
                <div className="versions-section">
                    <div className="section-header">
                        <button
                            className="create-version-button"
                            onClick={handleCreateVersion}
                            disabled={!canCreateVersion}
                            data-testid="create-version-button"
                        >
                            {i18n.translate('signDocVersionManagement.createNewVersionButton')}
                        </button>
                    </div>

                    <div className="versions-list" data-testid="versions-list">
                        {versions.length === 0 ? (
                            <div className="empty-versions" data-testid="empty-versions">
                                <p>{i18n.translate('signDocVersionManagement.noVersions')}</p>
                            </div>
                        ) : (
                            map(versions, (version) => (
                                <SignDocVersionCard
                                    key={version.id}
                                    version={version}
                                    onEdit={handleEditVersion}
                                    onRecurringAtUpdated={fetchVersions}
                                    isCurrentVersion={version.id === signDocId}
                                />
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Create Version Modal */}
            {isCreateModalOpen && signDoc && (
                <CreateEditNewSignDocModal
                    newDocSignModalOpen={isCreateModalOpen}
                    closeNewDocSignModal={handleCloseCreateModal}
                    onAfterEditComplete={handleAfterCreateComplete}
                    signDoc={signDoc}
                    editMode={false}
                    signDocType={signDoc.type}
                    forKnowledgeCenter={false}
                    forceCompletion={false}
                    isNewVersion={true}
                    totalVersionCount={versions.length}
                />
            )}

            {/* Edit Version Modal */}
            {isEditModalOpen && selectedVersion && (
                <CreateEditNewSignDocModal
                    newDocSignModalOpen={isEditModalOpen}
                    closeNewDocSignModal={handleCloseEditModal}
                    onAfterEditComplete={handleAfterEditComplete}
                    signDoc={selectedVersion}
                    editMode={true}
                    signDocType={selectedVersion.type}
                    forKnowledgeCenter={false}
                    forceCompletion={false}
                    totalVersionCount={versions.length}
                />
            )}
        </div>
    );
};
